// react
import { DragEvent, MouseEvent, useState } from 'react';

// mui
import {
	Button,
	ButtonProps,
	Checkbox,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Popover,
	PopoverProps,
} from '@mui/material';

// icons
import { DragIndicatorOutlined as IconDragIndicator } from '@mui/icons-material';

// props
export interface SortableListItem {
	checked?: boolean;
	key: string;
	label: string;
}
export interface BtnSortableListProps extends ButtonProps {
	items: SortableListItem[];
	label: string;
	onItemsChange: () => void;
	PopoverProps?: Partial<PopoverProps>;
	setItems: React.Dispatch<React.SetStateAction<SortableListItem[]>>;
}

export const BtnSortableList = ({
	items,
	label,
	onItemsChange,
	PopoverProps,
	setItems,
	...props
}: BtnSortableListProps) => {
	// state
	const [anchorMenu, setAnchorMenu] = useState<HTMLButtonElement | null>(null);
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
	const [itemDragged, setItemDragged] = useState<number | null>(null);

	const handleDragEnd = (e: DragEvent<HTMLLIElement>) => {
		e.preventDefault();
		setItemDragged(null);
		setDragOverIndex(null);

		if (onItemsChange) {
			onItemsChange();
		}
	};

	const handleDragStart = (e: DragEvent<HTMLLIElement>, index: number) => {
		setItemDragged(index);
	};

	const handleDragOver = (e: DragEvent<HTMLLIElement>, index: number) => {
		e.preventDefault();
		const target = e.currentTarget;
		const rect = target.getBoundingClientRect();
		const offset = e.clientY - rect.top;
		const middle = rect.height / 2;

		if (offset > middle) {
			setDragOverIndex(index + 1);
		} else {
			setDragOverIndex(index);
		}
	};

	const handleDrop = (e: DragEvent<HTMLLIElement>, index: number) => {
		e.preventDefault();
		e.stopPropagation();

		if (itemDragged !== null && dragOverIndex !== null && itemDragged !== dragOverIndex - 1) {
			const updatedColumns = [...items];
			const [removed] = updatedColumns.splice(itemDragged, 1);

			// adjust index based on specified conditions
			let insertIndex = dragOverIndex;
			if (itemDragged < dragOverIndex) {
				insertIndex--;
			}
			updatedColumns.splice(insertIndex, 0, removed);
			setItems?.(updatedColumns);
		}

		setItemDragged(null);
		setDragOverIndex(null);
	};

	const handleCheckboxToggle = (key: string) => {
		setItems((prev) =>
			prev.map((item) => (item.key === key ? { ...item, checked: !item.checked } : item))
		);
		if (onItemsChange) {
			onItemsChange();
		}
	};

	return (
		<>
			<Button
				{...props}
				onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorMenu(e.currentTarget)}>
				{label}
			</Button>
			<Popover
				anchorEl={anchorMenu}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				onClose={() => setAnchorMenu(null)}
				open={Boolean(anchorMenu)}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				{...PopoverProps}>
				<List dense={true}>
					{items.map((col, i) => {
						const isDragOver = dragOverIndex === i;
						const isChecked = items.find((i) => i.key === col.key)?.checked;
						return (
							<ListItem
								data-order={i}
								disablePadding={true}
								draggable={true}
								key={col.key}
								onDragEnd={handleDragEnd}
								onDragOver={(e) => handleDragOver(e, i)}
								onDragStart={(e) => handleDragStart(e, i)}
								onDrop={(e) => handleDrop(e, i)}
								secondaryAction={
									<IconButton edge="end">
										<IconDragIndicator />
									</IconButton>
								}
								sx={(theme) => {
									return {
										borderTop:
											isDragOver && dragOverIndex === i
												? `2px solid ${theme.palette.brand.blue[500]}`
												: 'none',
										borderBottom:
											isDragOver && dragOverIndex === i + 1
												? `2px solid ${theme.palette.brand.blue[500]}`
												: 'none',
									};
								}}>
								<ListItemButton>
									<ListItemIcon sx={{ minWidth: 0 }}>
										<Checkbox
											checked={isChecked}
											onChange={() => handleCheckboxToggle(col.key)}
											edge="start"
											tabIndex={-1}
											disableRipple
										/>
									</ListItemIcon>
									<ListItemText primary={`${i + 1}. ${col.label}`} />
								</ListItemButton>
							</ListItem>
						);
					})}
				</List>
			</Popover>
		</>
	);
};
